<template>
  <QuestionsCard
    :cards="cards"
    :resolved="resolved"
    :superseded="superseded"
    :rows-of="(question) => question.people"
    :value-of="identity"
    :key-of="(person) => String(person.id)"
    :marked="false"
    @submit="$emit('submit', $event)"
  >
    <template #row="{ row, selected }">
      <span class="person__avatar" aria-hidden="true">
        <i :class="roleIcon(row)"></i>
      </span>
      <span class="person">
        <span class="person__name">
          {{ row.name }}
          <span
            v-if="roleLabel(row)"
            class="person__role"
            :class="{ 'person__role--staff': row.type === 'provider' }"
            >{{ roleLabel(row) }}</span
          >
        </span>
        <span v-if="details(row).length" class="person__details">
          <span v-for="(d, i) in details(row)" :key="i">{{ d }}</span>
        </span>
      </span>
      <i
        v-if="selected"
        class="fas fa-check-circle person__check"
        aria-hidden="true"
      ></i>
    </template>
  </QuestionsCard>
</template>

<script>
import QuestionsCard from "@/components/OliverBotV2/interactions/shared/QuestionsCard.vue";
import helpers from "@/utils/helpers";

const ROLES = {
  provider: "Provider",
  client: "Patient",
  manager: "Manager",
  admin: "Admin",
};
const DATES = new Set(["date", "datetime"]);

/** A date as "Mar 4, 1986", or "" when there is none: MySQL's empty date, `0000-00-00`, would
 * otherwise read "Nov 30, 1899". */
function shownDate(value) {
  const day = String(value == null ? "" : value).slice(0, 10);
  if (!/^(?!0000)\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(day))
    return "";
  return helpers.prettyDate(day, "MMM D, YYYY");
}

/** Which person a name meant: each shown as the CRM returned them, picked by id. */
export default {
  name: "UserSelectionCard",
  components: { QuestionsCard },
  props: {
    cards: { type: Array, required: true },
    resolved: { type: Object, default: null },
    superseded: { type: Boolean, default: false },
  },
  emits: ["submit"],
  methods: {
    /** What a pick sends: who the person is, nothing private. */
    identity(person) {
      return {
        id: person.id,
        user_id: person.user_id,
        name: person.name,
        type: person.type,
      };
    },
    roleLabel(person) {
      const type = String(person.type || "").toLowerCase();
      return type ? ROLES[type] || "Staff" : "";
    },
    roleIcon(person) {
      if (person.type === "provider") return "fas fa-user-md";
      if (person.type === "manager" || person.type === "admin")
        return "fas fa-user-shield";
      return "fas fa-user";
    },
    /** Each field the query returned, as the CRM words it. */
    details(person) {
      return (person.details || [])
        .map((d) => {
          if (DATES.has(d.type)) {
            const date = shownDate(d.value);
            return date && `${d.label} ${date}`;
          }
          if (typeof d.value === "boolean") {
            return `${d.label}: ${d.value ? "Yes" : "No"}`;
          }
          if (d.type === "code") return String(d.value);
          return `${d.label}: ${d.value}`;
        })
        .filter(Boolean);
    },
  },
};
</script>

<style lang="scss" scoped>
.person__avatar {
  flex: 0 0 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(127, 127, 127, 0.18);
  color: var(--oliver-text-muted);
}

.person {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.person__name {
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.person__role {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;
  background: rgba(127, 127, 127, 0.18);
  color: var(--oliver-text-muted);

  &--staff {
    background: var(--oliver-indicator-bg);
    color: var(--oliver-accent);
  }
}

.person__details {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 11px;
  color: var(--oliver-text-muted);
  overflow-wrap: anywhere;
}

.person__check {
  flex: 0 0 auto;
  margin-left: auto;
  color: var(--oliver-accent);
}
</style>
